import {
  Component,
  ChangeDetectorRef,
  Input,
  Renderer2,
  EventEmitter,
  Output,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from '@angular/core';
import { Location } from '@angular/common';
import { SubscriptionLike } from 'rxjs';
import { HeicPreviewService, isHeicImage } from './heic-preview.service';
import { downloadAttachment } from '../download-attachment';

@Component({
  selector: 'ecpl-image-viewer',
  templateUrl: './ecpl-image-viewer.component.html',
  styleUrls: ['./ecpl-image-viewer.component.css'],
  standalone: false
})
export class EcplImageViewerComponent implements OnChanges, OnDestroy {
  @Input() id: string;
  @Input() imgSrc: string;
  @Input() imgName: string;
  @Input() imgType: string;
  @Input() isDialog: boolean;
  @Input() forceReopen: number = 0; // Changes every time file is selected
  @Output() close = new EventEmitter<void>();

  showModalWindow = false;
  previewSrc = '';
  isLoading = false;
  previewFailed = false;
  private objectUrl: string | undefined;
  private previewRequest = new AbortController();
  private readonly navigation: SubscriptionLike;

  constructor(
    private renderer: Renderer2,
    location: Location,
    private readonly previews: HeicPreviewService,
    private readonly changeDetector: ChangeDetectorRef
  ) {
    this.navigation = location.subscribe(() => {
      if (this.showModalWindow) {
        this.hideModal();
      }
    });
  }

  get isHeic(): boolean {
    return isHeicImage(this.imgType, this.imgSrc, this.imgName);
  }

  showModal() {
    this.showModalWindow = true;
    this.renderer.addClass(document.body, 'modal-open');
    if (this.isHeic) {
      if (!this.isLoading && !this.previewSrc) {
        void this.loadHeicPreview();
      }
    } else {
      this.previewSrc = this.imgSrc;
    }
    this.changeDetector.markForCheck();
  }

  hideModal() {
    this.showModalWindow = false;
    this.renderer.removeClass(document.body, 'modal-open');
    this.releasePreview();
    this.changeDetector.markForCheck();
    this.close.emit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('imgSrc' in changes || 'imgType' in changes || 'imgName' in changes) {
      this.releasePreview();
    }
    if ('isDialog' in changes && !this.isDialog && this.showModalWindow) {
      this.hideModal();
    } else if (this.imgSrc && (this.isDialog || this.showModalWindow)) {
      this.showModal();
    }
  }

  downloadOriginal(): void {
    void downloadAttachment(this.imgSrc, this.imgName);
  }

  ngOnDestroy(): void {
    this.navigation.unsubscribe();
    this.releasePreview();
    if (this.showModalWindow) {
      this.renderer.removeClass(document.body, 'modal-open');
    }
  }

  private async loadHeicPreview(): Promise<void> {
    const request = new AbortController();
    this.previewRequest = request;
    this.isLoading = true;
    this.previewFailed = false;
    try {
      const preview = await this.previews.createPreview(
        this.imgSrc,
        request.signal
      );
      if (request.signal.aborted) {
        return;
      }
      this.objectUrl = URL.createObjectURL(preview);
      this.previewSrc = this.objectUrl;
    } catch {
      if (!request.signal.aborted) {
        this.previewFailed = true;
      }
    } finally {
      if (!request.signal.aborted) {
        this.isLoading = false;
        this.changeDetector.markForCheck();
      }
    }
  }

  private releasePreview(): void {
    this.previewRequest.abort();
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = undefined;
    }
    this.previewSrc = '';
    this.isLoading = false;
    this.previewFailed = false;
  }
}
